import { QueryOptions, useMutation, useQuery, useQueryClient } from "react-query";
import api from ".";
import { CategoryType } from "../../dto/Category.dto";
import { CategoriesResponse, CategoryPaginationOptions, CreateCategory } from "../../dto/request/categories-request.dto";
import { ApiServiceErr, MutOpt } from "../../types/api.types";

export const useGetCategories = (
    { limit = 10, page = 1, type, status }: CategoryPaginationOptions,
    opt?: QueryOptions<CategoriesResponse>
) => {

    return useQuery<CategoriesResponse, ApiServiceErr>(
        ['categories', limit, page, type, status],
        async () => {
            let categoryType =type;
            if (type && type === CategoryType.All) {
                categoryType = undefined;
            }
            
            const response = await api.get<CategoriesResponse>(
                `/admin/category`,
                {
                    params: {
                        limit,
                        page,
                        type: categoryType,
                        status,
                    }
                }
            );
            return {...response, limit: limit};
        },
        opt,
    );
};

export const useCreateCategory = (opt?: MutOpt<any>) => {
    const queryClient = useQueryClient();

    return useMutation<any, ApiServiceErr, CreateCategory>(async (data: CreateCategory) => {
        const response = await api.post(`/admin/category`, data);
        return response.data;
    },
        {
            ...opt,
            onSuccess: (data: any, variables: unknown, context: unknown) => {
                queryClient.invalidateQueries("watchfaces");
                opt?.onSuccess?.(data, variables, context);
            },
        });
}

export const useUpdateCategory = (opt?: MutOpt<any>) => {
    const queryClient = useQueryClient();

    return useMutation<any, ApiServiceErr, { id: string, data: CreateCategory }>(async ({ id, data }) => {

        const response = await api.put(`/admin/category/${id}`, data);
        return response.data;
    }, {
        ...opt,
        onSuccess: (data: any, variables: {
            id: string;
            data: CreateCategory;
        }, context: unknown) => {
            queryClient.invalidateQueries("categories");
            opt?.onSuccess?.(data, variables, context);
        }
    });
}

export const useDeleteCategory = (opt?: MutOpt<any>) => {
    const queryClient = useQueryClient();

    return useMutation<any, ApiServiceErr, string>(async (id) => {

        const response = await api.delete(`/admin/category/${id}`);
        return response;
    }, {
        ...opt,
        onSuccess: (data: any, id: string, context: unknown) => {
            queryClient.invalidateQueries("categories");
            opt?.onSuccess?.(data, id, context);
        }
    });
}

export const useGetCategoryCount = (
    { type }: { type?: string },
    opt?: QueryOptions<{ count: number }>
) => {
    return useQuery<{ count: number }, ApiServiceErr>(
        ['category-count', type],
        async () => {
            const response = await api.get<{ count: number }>(
                `/admin/category/count`,
                {
                    params: {
                        type,
                    }
                }
            );
            return response;
        },
        {
            ...opt,
            enabled: type !== undefined,
        }
    );
}

export const useGetCategoryDefaultPosition = (
    { categoryId }: { categoryId: string },
    opt?: QueryOptions<{ position: number }> & { enabled?: boolean }
) => {
    return useQuery<{ position: number }, ApiServiceErr>(
        ['category-default-position', categoryId],
        async () => {
            const response = await api.get<{ position: number }>(
                `/admin/category/${categoryId}/default-position`
            );
            return response;
        },
        {
            ...opt,
            enabled: opt?.enabled !== undefined ? opt.enabled : Boolean(categoryId),
        }
    );
}